import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { TeamMember } from '../team-member';
import { useTeam } from '../team-members/team-member-context';

export type Round = {
  turn: number;
  pairs: any[]
}

type ContextState = {
  matrix: Array<Array<any>>,
  round: Round[],
  removePair: (index: number) => void
  computePair: (i: number, j: number) => void
  startRound: () => void
  isCurrentPairing: (teamMemberTuple: any) => boolean
  currentPairs: Round[],
}

const PairMatrixContext = createContext<ContextState | undefined>(undefined);

export const PairMatrixProvider = ({ children }: { children: ReactNode }) => {
  const [round, setRound] = useState<Round[]>([]);
  const { teamMembers } = useTeam();
  const [matrix, setMatrix] = useState<Array<Array<any>>>([]);
  const [currentPairs, setCurrentPairs] = useState<Round[]>([]);

  useEffect(() => {
    function buildMatrix(teams: TeamMember[]) {
      const members = teams.length;
      const matrix = new Array(members);

      for (let i = 0; i < members; i++) {
        matrix[i] = new Array(members);

        for (let j = 0; j < members; j++) {
          matrix[i][j] = new Array(members);
        }
      }

      for (let i = 0; i < members; i++) {
        for (let j = 0; j < members; j++) {
          matrix[i][j] = [teams[i], teams[j]];
        }
      }

      return matrix;
    }

    setMatrix(buildMatrix(teamMembers))
  }, [teamMembers])

  const removePair = (index: number): void => {
    const pairs: any = [...currentPairs];

    pairs.splice(index, 1);

    setCurrentPairs(pairs);
  }

  const computePair = (i: number, j: number): void => {
    const pairs: any = [...currentPairs];

    pairs.push([
      matrix[i][j][0],
      matrix[i][j][1],
    ]);

    setCurrentPairs(pairs);
  };

  const startRound = (): void => {
    const finishRound = round?.length || 0

    const currentRound: Round = { turn: finishRound, pairs: [] }
    currentRound.pairs = [...currentPairs];

    setRound([
      ...round,
      currentRound,
    ]);

    setCurrentPairs([]);
  }

  const isCurrentPairing = (teamMemberTuple: any): boolean => {
    const flat = currentPairs.flat();

    const fndCurrentPair = flat.filter((pair: any) => {
      return pair.id === teamMemberTuple[0].id || pair.id === teamMemberTuple[1].id;
    });

    return fndCurrentPair.length >= 1;
  }

  const pairMatrixState: ContextState = {
    round,
    removePair,
    computePair,
    startRound,
    isCurrentPairing,
    currentPairs,
    matrix
  }

  return (
    <PairMatrixContext.Provider value={pairMatrixState}>
      {children}
    </PairMatrixContext.Provider>
  );
};

export const usePairMatrix = () => {
  const context = useContext(PairMatrixContext)
  if (context === undefined) {
    throw new Error('usePairMatrix must be used within a PairMatrixProvider')
  }
  return context
}